import type { CSSProperties } from 'react';

import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Tooltip } from '@sravni/react-design-system/lib/Tooltip';
import { Tooltip as TooltipIcon } from '@sravni/react-icons';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { Currency } from '@src/@types/currency';
import { UNBREAKABLE_SEPARATOR } from '@src/constants/unbreakableSeparator';
import { getAmountString } from '@src/helpers/common';
import type { LightweightCardContentSettings } from 'entities/Microcredits/ui/LightweightCard';
import { CardApprovalProbability, getOfferRate, getCardTerm } from 'entities/Microcredits/ui/LightweightCard';
import { getPskRange } from 'entities/Microcredits/ui/LightweightCard/lib/getPskRange';

import styles from './LightweightCard.module.scss';

export const getCardContent = (
  { details, rates, approvalProbability }: IClientMicrocreditListItem,
  currency: Currency,
  isPhone: boolean,
  contentSettings: LightweightCardContentSettings,
) => {
  const content = [];

  if (contentSettings.amount.visible) {
    content.push({
      value: getAmountString(details?.amount?.to || details?.amount?.from, currency),
      label: 'Сумма до',
      ...contentSettings.amount,
    });

    if (contentSettings.term.visible) {
      content.push({
        value: getCardTerm(details?.term),
        label: 'Срок (дни)',
        ...contentSettings.term,
      });
    }

    if (contentSettings.psk.visible) {
      content.push({
        value: getPskRange(details.psk, isPhone),
        label: (
          <>
            <span>ПСК</span>
            <Tooltip
              placement="bottom-end"
              closable
              title="Полная стоимость кредита"
              content={`По закону «О рекламе» № 38-ФЗ мы${UNBREAKABLE_SEPARATOR}обязаны указывать полную стоимость кредита (ПСК) — сумму ваших реальных расходов по кредиту, включая ставку, страховку и стоимость других доп. услуг. Значения ПСК получены из открытых источников. Итоговая процентная ставка и ПСК будут указаны в кредитном договоре и${UNBREAKABLE_SEPARATOR}рассчитываются индивидуально`}
            >
              <Icon size={16} icon={<TooltipIcon />} />
            </Tooltip>
          </>
        ),
        labelClassName: styles.pskField,
        ...contentSettings.psk,
      });
    }

    if (contentSettings.rate.visible) {
      content.push({
        value: getOfferRate(rates),
        label: 'Ставка',
        ...contentSettings.rate,
      });
    }

    if (contentSettings.approvalProbability.visible) {
      content.push({
        value: <CardApprovalProbability category={approvalProbability?.category} isNewDesignCard={false} />,
        label: 'Одобрение',
        ...contentSettings.approvalProbability,
      });
    }
  }

  return content;
};

export const DEFAULT_CONTENT: LightweightCardContentSettings = {
  psk: { visible: true },
  amount: {
    visible: true,
  },
  term: {
    visible: true,
  },
  rate: {
    visible: true,
  },
  badge: {
    visible: true,
  },
  approvalProbability: {
    visible: true,
  },
};

export const PIXEL_DISPLAY_WRAPPER_STYLES: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
};
